import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';

import back from '../../../../../../../../images/back.svg';
import { Button, notification, Tooltip } from 'antd';
import ReportForms from './ReportForms';
import { useState } from 'react';
import { useVersionControl } from './ReportForms/hooks/useVersionControl';
import VersionSelector from './ReportForms/components/VersionSelector';
import { signTasks } from '../../../../../../../../helpers/apiCalls';
import TaskReportService from '../../../../../../../../../services/TaskReportService';
import ReportTranslationService from '../../../../../../../../../services/ReportTranslationService';
import useGlobalState from '../../../../../../../../../Template/atoms/variables';
import useTemplateState from '../../../../../../../../../Template/atoms/variables';

const ReportPage = ({ setReportOpen, report, setReport, setForceReload }) => {
  const { t, i18n } = useTranslation();

  const [notificationApi, contextHolder] = notification.useNotification();

  const [editing, setEditing] = useState(false);
  const [originalReport, setOriginalReport] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(() => {
    const versions = report?.report?.versions;
    return versions && versions.length > 0 ? versions[0].version : 1;
  });

  const { instance } = useMsal();
  const { setPendingApiCalls } = useGlobalState();
  const { companyInfo, user } = useTemplateState();

  const account = instance.getActiveAccount();

  const { given_name: givenName, family_name: familyName, name } = account.idTokenClaims || {};
  const currentUser = name ?? `${givenName} ${familyName}`;

  const {
    handleSave,
    handleCancel,
    handleVersionChange,
    handlePhaseValueChange,
  } = useVersionControl({
    report,
    setReport,
    originalReport,
    selectedVersion,
    setSelectedVersion,
    notificationApi,
    currentUser,
    setForceReload,
  });

  useEffect(() => {
    const versions = report?.report?.versions;
    if (versions && versions.length > 0) {
      setSelectedVersion(versions[0].version);
    }
  }, [report]);

  // Get the current version's report
  const getCurrentVersionReport = () => {
    const versions = report?.report?.versions;
    const firstVersion = versions && versions.length > 0 ? versions[0].version : 1;
    
    if (selectedVersion === firstVersion) {
      return report;
    }
    
    const version = versions?.find(v => v.version === selectedVersion);
    if (version) {
      return {
        ...report,
        phase: version.content
      };
    }
    
    return report;
  };

  const currentVersionReport = getCurrentVersionReport();

  useEffect(() => {
    if (editing) {
      setOriginalReport(JSON.parse(JSON.stringify(report)));
    }
  }, [editing]);

  // Adapted functions for single report
  const handleBulkDownload = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['downloadReports']));
      const translatedReport = ReportTranslationService.translateReport(currentVersionReport, i18n.language);
      // Single report download should not be zipped
      await TaskReportService.build(
        [translatedReport],
        companyInfo?.logo,
        false,
        companyInfo?.reportFilenamePattern,
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      notificationApi.error({
        message: t('error'),
        description: t('error_generating_pdf'),
        duration: 5,
      });
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'downloadReports'));
    }
  };

  const handleApproveTask = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['approveTasks']));
      const signedTasks = await signTasks(instance, [report._id]);
      if (signedTasks && signedTasks.length > 0) {
        setReport(signedTasks[0]);
      }
      setForceReload(true);
      notificationApi.success({
        message: t('success'),
        description: t('report_approved_successfully'),
        duration: 3,
      });
    } catch (error) {
      console.error('Error approving task:', error);
      notificationApi.error({
        message: t('error'),
        description: t('error_approving_report'),
        duration: 5,
      });
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'approveTasks'));
    }
  };

  const handleRejectTask = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['rejectTasks']));
      const signedTasks = await signTasks(instance, [report._id], 'reject');
      if (signedTasks && signedTasks.length > 0) {
        setReport(signedTasks[0]);
      }
      setForceReload(true);
      notificationApi.success({
        message: t('success'),
        description: t('report_rejected_successfully'),
        duration: 3,
      });
    } catch (error) {
      console.error('Error rejecting task:', error);
      notificationApi.error({
        message: t('error'),
        description: t('error_rejecting_report'),
        duration: 5,
      });
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'rejectTasks'));
    }
  };

  // Helper functions for button states
  const canDownload = () => {
    return report?.status !== 'Pending' && report?.status !== 'Expired';
  };

  const canApprove = () => {
    const reportStatus = report?.report?.status?.toLowerCase();
    const taskStatus = report?.status?.toLowerCase();
    
    return (
      reportStatus !== 'approved' &&
      reportStatus !== 'rejected' &&
      reportStatus !== 'n/a' &&
      taskStatus !== 'pending' &&
      user?.access?.some((access) => ['planner', 'management', 'quality'].includes(access))
    );
  };

  const canReject = () => {
    const reportStatus = report?.report?.status?.toLowerCase();
    const taskStatus = report?.status?.toLowerCase();
    
    return (
      reportStatus !== 'approved' &&
      reportStatus !== 'rejected' &&
      reportStatus !== 'n/a' &&
      taskStatus !== 'pending' &&
      taskStatus !== 'expired' &&
      user?.access?.some((access) => ['planner', 'management', 'quality'].includes(access))
    );
  };

  const getDownloadTooltip = () => {
    if (!canDownload()) return t('pending_tasks_cannot_be_downloaded');
    return '';
  };

  const getApproveTooltip = () => {
    const reportStatus = report?.report?.status?.toLowerCase();
    const taskStatus = report?.status?.toLowerCase();
    
    if (reportStatus === 'approved' || reportStatus === 'rejected') {
      return t('report_is_final');
    }
    if (taskStatus === 'pending') {
      return t('task_is_pending');
    }
    if (!user?.access?.some((access) => ['planner', 'management', 'quality'].includes(access))) {
      return t('profile_cannot_approve');
    }
    return '';
  };

  const getRejectTooltip = () => {
    const reportStatus = report?.report?.status?.toLowerCase();
    const taskStatus = report?.status?.toLowerCase();
    
    if (reportStatus === 'approved' || reportStatus === 'rejected') {
      return t('report_is_final');
    }
    if (taskStatus === 'pending' || taskStatus === 'expired') {
      return t('task_is_pending');
    }
    if (!user?.access?.some((access) => ['planner', 'management', 'quality'].includes(access))) {
      return t('profile_cannot_reject');
    }
    return '';
  };

  /**
   * Determina se a edição do relatório deve ser desabilitada
   *
   * A edição é desabilitada nas seguintes situações:
   * 1. Quando uma versão anterior está selecionada (selectedVersion !== report?.report?.latestVersion)
   *    - Apenas a versão atual (latestVersion) pode ser editada
   *    - Versões anteriores são somente leitura
   *
   * 2. Quando o status do relatório não é 'Pending Approval'
   *    - Relatórios aprovados não podem ser editados
   *    - Relatórios reprovados APENAS podem ser editados se for reprovação automática
   *    - Apenas relatórios pendentes de aprovação podem ser modificados
   *
   * 3. Quando o relatório possui algum checklist errado
   *    - Apenas relatórios com erro na validação da imagem podem ser editados
   *
   * @returns {boolean} true se a edição deve ser desabilitada, false caso contrário
   */
  function disableEditReport() {
    
    // report.status = task status
    // report.report.status = report status
    
    if (report?.report?.status === "Ongoing approval" ||
        report?.report?.status === "Rejected" || report?.report?.status === "Approved") {
          return true
        } else {
          return false;
        }

  }

    return (
    <div className='reportContainer'>
      {contextHolder}
      <div className='reportHeader'>
        <div
          className='reportTitleContainer'
          onClick={() => {
            if (!editing) setReportOpen(false);
          }}
          style={{ cursor: editing ? 'not-allowed' : '', opacity: editing ? 0.5 : 1 }}
        >
          <div className='backArrow'>
            <img src={back} />
          </div>
          <div className='title'>{t('back')}</div>
        </div>
        <div className='controls'>
          {!editing && (
            <VersionSelector
              versions={report?.report?.versions}
              currentVersion={selectedVersion}
              onVersionChange={({ target }) => handleVersionChange(target.value)}
            />
          )}
          
          {/* Action buttons */}
          {!editing && (
            <>
              <Tooltip title={getDownloadTooltip()}>
                <Button
                  size='large'
                  disabled={!canDownload()}
                  style={{
                    borderColor: 'black',
                    background: 'white',
                    color: 'black',
                    opacity: canDownload() ? 1 : 0.5,
                    marginRight: '8px'
                  }}
                  onClick={handleBulkDownload}
                >
                  {t('download')}
                </Button>
              </Tooltip>
              
              <Tooltip title={getApproveTooltip()}>
                <Button
                  size='large'
                  disabled={!canApprove()}
                  style={{
                    borderColor: '#1777FF',
                    background: '#1777FF',
                    color: 'white',
                    opacity: canApprove() ? 1 : 0.5,
                    marginRight: '8px'
                  }}
                  onClick={handleApproveTask}
                >
                  {t('approve')}
                </Button>
              </Tooltip>
              
              <Tooltip title={getRejectTooltip()}>
                <Button
                  size='large'
                  disabled={!canReject()}
                  style={{
                    borderColor: '#FF4D4F',
                    background: '#FF4D4F',
                    color: 'white',
                    opacity: canReject() ? 1 : 0.5,
                    marginRight: '8px'
                  }}
                  onClick={handleRejectTask}
                >
                  {t('reject')}
                </Button>
              </Tooltip>
            </>
          )}
          
          {editing && (
            <Button
              size='large'
              style={{
                borderColor: '#333333',
                background: '#333333',
                color: 'white',
              }}
              onClick={() => {
                setEditing(false);
                handleCancel();
              }}
            >
              {t('cancel-btn')}
            </Button>
          )}
          <Button
            size='large'
            style={{
              borderColor: disableEditReport() ? 'white' : '#1777FF',
              background: disableEditReport() ? 'grey' : '#1777FF',
              color: 'white',
            }}
            disabled={disableEditReport()}
            onClick={() => {
              if (editing) {
                handleSave();
              }
              setEditing(!editing);
            }}
          >
            {editing ? t('save') : t('edit-btn')}
          </Button>
        </div>
      </div>
      <ReportForms
        editing={editing}
        report={currentVersionReport}
        handlePhaseValueChange={handlePhaseValueChange}
      />
    </div>
  );
};

export default ReportPage;
